import React, { useEffect, useState, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Grid, Paper, Skeleton, Chip, Divider, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Alert, Stack, LinearProgress,
} from "@mui/material";
import RssFeedIcon            from "@mui/icons-material/RssFeed";
import FilterAltIcon          from "@mui/icons-material/FilterAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import CopyAllIcon            from "@mui/icons-material/CopyAll";
import BoltIcon               from "@mui/icons-material/Bolt";
import DeleteSweepIcon        from "@mui/icons-material/DeleteSweep";
import BarChartIcon           from "@mui/icons-material/BarChart";
import RefreshIcon            from "@mui/icons-material/Refresh";
import MonitorHeartIcon       from "@mui/icons-material/MonitorHeart";
import { isAutheticated } from "../auth/helper";
import {
  getAllSubAdmins, getArticleStats, getDedupStats,
  getPipelineStats, purgeArticles, purgeDedupLogs, getSettings,
} from "./helper/adminapicall";

// ── Stat card ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon, title, value, sub, color, loading }) => (
  <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>{title}</Typography>
        {loading
          ? <Skeleton variant="text" width={60} height={40} />
          : <Typography variant="h4" sx={{ fontWeight: 700 }}>{value ?? "—"}</Typography>
        }
        {sub && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>{sub}</Typography>
        )}
      </Box>
      <Box sx={{
        width: 48, height: 48, borderRadius: "12px",
        bgcolor: `${color}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
      </Box>
    </Box>
  </Paper>
);

// ── Dashboard ──────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { admin, token } = isAutheticated();

  const [stats,         setStats]         = useState(null);
  const [dedup,         setDedup]         = useState(null);
  const [pipeline,      setPipeline]      = useState([]);
  const [statsLoading,  setStatsLoading]  = useState(true);
  const [dedupLoading,  setDedupLoading]  = useState(true);
  const [pipeLoading,   setPipeLoading]   = useState(true);

  // Purge dialog state
  const [purgeTarget,  setPurgeTarget]  = useState(null);  // null | "articles" | "dedup"
  const [purgeDays,    setPurgeDays]    = useState("30");  // "" = purge all
  const [purging,      setPurging]      = useState(false);
  const [purgeResult,  setPurgeResult]  = useState(null);  // { deleted, error }

  const ARTICLE_PURGE_PRESETS = [
    { label: "1 month",  value: "30"  },
    { label: "3 months", value: "90"  },
    { label: "6 months", value: "180" },
    { label: "1 year",   value: "365" },
    { label: "All",      value: ""    },
  ];

  const DEDUP_PURGE_PRESETS = [
    { label: "7 days",  value: "7"  },
    { label: "14 days", value: "14" },
    { label: "30 days", value: "30" },
    { label: "90 days", value: "90" },
    { label: "All",     value: ""   },
  ];

  useEffect(() => {
    // Midnight in the browser's local timezone (e.g. IST) as ISO string
    const localMidnight = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); })();

    const loadStats = async () => {
      try {
        const [subRes, artRes] = await Promise.allSettled([
          getAllSubAdmins(admin._id, token),
          getArticleStats(admin._id, token, localMidnight),
        ]);
        const art = artRes.status === "fulfilled" ? artRes.value : {};
        setStats({
          subadmins:     (subRes.status === "fulfilled" && Array.isArray(subRes.value)) ? subRes.value.length : 0,
          active_feeds:  art.active_feeds     ?? 0,
          today:         art.articles_today   ?? 0,
          breaking_today:art.breaking_today   ?? 0,
          total_articles:art.total_articles   ?? 0,
          total_feeds:   art.total_feeds      ?? 0,
          total_groups:  art.total_groups     ?? 0,
          dedup_today:   art.dedup_caught_today ?? 0,
          top_sources:   art.top_sources      ?? [],
        });
      } finally {
        setStatsLoading(false);
      }
    };

    const loadDedup = async () => {
      try {
        const d = await getDedupStats(admin._id, token, localMidnight);
        if (!d?.error) setDedup(d);
      } finally {
        setDedupLoading(false);
      }
    };

    loadStats();
    loadDedup();
    getSettings(admin._id, token).then(d => {
      if (d && typeof d.ingestion_paused === "boolean") setIngestionPaused(d.ingestion_paused);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [bgeRetryCount,  setBgeRetryCount]  = useState(0);
  const [ingestionPaused,setIngestionPaused] = useState(false);

  const loadPipeline = useCallback(async () => {
    setPipeLoading(true);
    try {
      const d = await getPipelineStats(admin._id, token);
      // API returns { rows: [...], bge_retry_count: N }
      if (d?.rows) {
        setPipeline(d.rows);
        setBgeRetryCount(d.bge_retry_count || 0);
      } else if (Array.isArray(d)) {
        setPipeline(d); // backwards compat
      }
    } finally {
      setPipeLoading(false);
    }
  }, [admin._id, token]);

  // Load pipeline stats on mount + auto-refresh every 30s
  useEffect(() => {
    loadPipeline();
    const id = setInterval(loadPipeline, 30_000);
    return () => clearInterval(id);
  }, [loadPipeline]);

  const handlePurge = async () => {
    setPurging(true); setPurgeResult(null);
    try {
      const body = purgeDays ? { older_than_days: Number(purgeDays) } : {};
      const res  = purgeTarget === "articles"
        ? await purgeArticles(admin._id, token, body)
        : await purgeDedupLogs(admin._id, token, body);
      if (res?.error) setPurgeResult({ error: res.error });
      else            setPurgeResult({ deleted: res.deleted });
    } finally {
      setPurging(false);
    }
  };

  const cards = [
    { title: "Active Feeds",   value: stats?.active_feeds,   sub: "configured RSS feeds", icon: <RssFeedIcon />,            color: "#3b82f6" },
    { title: "Articles Today", value: stats?.today,           sub: "ingested today",        icon: <CopyAllIcon />,            color: "#10b981" },
    { title: "Breaking Today", value: stats?.breaking_today,  sub: "breaking alerts",       icon: <BoltIcon />,               color: "#f59e0b" },
    { title: "Dedup Today",    value: stats?.dedup_today,     sub: "duplicates caught",     icon: <FilterAltIcon />,          color: "#ec4899" },
  ];

  // Merge articles_by_group + by_group from dedup stats
  const groupRows = React.useMemo(() => {
    if (!dedup) return [];
    const map = {};
    (dedup.articles_by_group || []).forEach(r => {
      map[r.group_id] = { group_name: r.group_name, articles: r.count, dedup: 0 };
    });
    (dedup.by_group || []).forEach(r => {
      if (map[r.group_id]) map[r.group_id].dedup = r.dedup_count;
      else map[r.group_id] = { group_name: r.group_name, articles: 0, dedup: r.dedup_count };
    });
    return Object.values(map).sort((a, b) => b.articles - a.articles);
  }, [dedup]);

  const timeAgo = (iso) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000)    return "just now";
    if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  const dedupByMethod = dedup?.by_method || [];
  const totalDedup    = dedup?.total_today ?? 0;

  return (
    <Base>
      {/* ── System status banners ── */}
      {ingestionPaused && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Ingestion Paused</strong> — all feed polling is stopped. Go to Feed Sources to resume.
        </Alert>
      )}
      {bgeRetryCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{bgeRetryCount} article{bgeRetryCount > 1 ? "s" : ""} queued in BGE retry</strong>
          {" "}— BGE may be down. Articles are saved but held from the live stream until BGE recovers.
        </Alert>
      )}

      {/* ── Heading ── */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>Ingest Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back, {admin?.name}. Here is today's feed ingestion overview.
        </Typography>
      </Box>

      {/* ── Stat cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} lg={3} key={card.title}>
            <StatCard {...card} loading={statsLoading} />
          </Grid>
        ))}
      </Grid>

      {/* ── Today's breakdown: dedup stats ── */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BarChartIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="h6">Today's Breakdown</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {dedupByMethod.map(m => (
              <Chip
                key={m.method}
                label={`${m.method.toUpperCase()} ${m.count}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
            {totalDedup > 0 && (
              <Chip
                label={`${totalDedup} total dedup`}
                size="small"
                color="secondary"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {dedupLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)
        ) : groupRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No ingestion data yet for today
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Articles ingested</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Duplicates caught</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Dedup rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupRows.map(row => {
                  const rate = row.articles + row.dedup > 0
                    ? Math.round((row.dedup / (row.articles + row.dedup)) * 100)
                    : 0;
                  return (
                    <TableRow key={row.group_name} hover>
                      <TableCell>{row.group_name}</TableCell>
                      <TableCell align="right">{row.articles.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.dedup.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                          <LinearProgress
                            variant="determinate"
                            value={rate}
                            color={rate > 50 ? "warning" : "primary"}
                            sx={{ width: 60, height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption">{rate}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Top Sources (last 24h) ── */}
      {(statsLoading || (stats?.top_sources?.length ?? 0) > 0) && (
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Top Sources — Last 24h</Typography>
          <Divider sx={{ mb: 2 }} />
          {statsLoading ? (
            Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Articles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.top_sources.map((s, i) => (
                    <TableRow key={s.source_code} hover>
                      <TableCell sx={{ color: "text.disabled", width: 32 }}>{i + 1}</TableCell>
                      <TableCell>{s.source_name || s.source_code || "—"}</TableCell>
                      <TableCell>
                        <Chip label={s.source_code} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 18 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{s.count.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ── System Status ── */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">System Status</Typography>
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
            label="All systems operational"
            size="small" color="success" variant="outlined"
            sx={{ fontSize: "0.72rem" }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[
            { label: "Ingest Worker", status: "Running",   color: "success" },
            { label: "Dedup Engine",  status: "Active",    color: "success" },
            { label: "Redis Cache",   status: "Connected", color: "success" },
            { label: "Database",      status: "Connected", color: "success" },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
              <Chip label={s.status} size="small" color={s.color} sx={{ mt: 0.3, fontSize: "0.7rem", height: 20 }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ── Pipeline Monitor ── */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MonitorHeartIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="h6">Pipeline Monitor</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>auto-refreshes every 30s</Typography>
          </Box>
          <IconButton size="small" onClick={loadPipeline} disabled={pipeLoading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {pipeLoading && pipeline.length === 0 ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)
        ) : pipeline.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No poll data yet — stats appear after the first feed poll completes
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Last poll</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Fetched</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saved</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Age skip</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Filter skip</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">GUID dedup</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">BGE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pipeline.map((row) => {
                  const saveRate = row.fetched > 0 ? Math.round((row.saved / row.fetched) * 100) : 0;
                  return (
                    <TableRow key={row.feed_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }} noWrap>{row.feed_title}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.disabled">{timeAgo(row.polled_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">{row.fetched}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifyContent: "flex-end" }}>
                          <LinearProgress variant="determinate" value={saveRate}
                            color={saveRate > 30 ? "success" : saveRate > 0 ? "warning" : "error"}
                            sx={{ width: 40, height: 4, borderRadius: 2 }} />
                          <Typography variant="caption" color={row.saved > 0 ? "success.main" : "text.secondary"}>
                            {row.saved}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color={row.age > 0 ? "warning.main" : "text.disabled"}>
                          {row.age > 0 ? row.age : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color={row.filter > 0 ? "error.main" : "text.disabled"}>
                          {row.filter > 0 ? row.filter : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.disabled">{row.guid > 0 ? row.guid : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" gap={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                          {Number(row.bge_err) > 0 && (
                            <Chip size="small" label={`ERR ${row.bge_err}`} color="error"
                              sx={{ height: 16, fontSize: "0.62rem", "& .MuiChip-label": { px: 0.8 } }} />
                          )}
                          {Number(row.bge_ok) > 0 && (
                            <Typography variant="caption" color="text.secondary">{row.bge_ok} ok</Typography>
                          )}
                          {Number(row.bge) > 0
                            ? <Typography variant="caption" color="info.main">{row.bge} dup</Typography>
                            : Number(row.bge_ok) === 0 && Number(row.bge_err) === 0
                              ? <Typography variant="caption" color="text.disabled">—</Typography>
                              : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Maintenance ── */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <DeleteSweepIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="h6">Maintenance</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Articles Collection</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Total stored: <strong>{stats?.total_articles?.toLocaleString() ?? "—"}</strong>
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1.5 }}>
              No auto-purge — click below to manually clean up
            </Typography>
            <Button
              variant="outlined" color="error" size="small"
              startIcon={<DeleteSweepIcon />}
              onClick={() => { setPurgeTarget("articles"); setPurgeDays("365"); setPurgeResult(null); }}
            >
              Purge Articles…
            </Button>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Dedup Logs</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Total stored:{" "}
              <strong>
                {dedupLoading ? "…" : (dedup?.total_dedup_logs ?? 0).toLocaleString()}
              </strong>
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1.5 }}>
              Tracks duplicate articles caught by URL and BGE checks.
            </Typography>
            <Button
              variant="outlined" color="error" size="small"
              startIcon={<DeleteSweepIcon />}
              onClick={() => { setPurgeTarget("dedup"); setPurgeDays("30"); setPurgeResult(null); }}
            >
              Purge Dedup Logs…
            </Button>
          </Paper>
        </Stack>
      </Paper>

      {/* ── Account ── */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Account</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Typography variant="body2" fontWeight={500}>{admin?.name} {admin?.lastname}</Typography>
          </Box>
          <Box sx={{ mx: 2, borderLeft: "1px solid", borderColor: "divider" }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="body2" fontWeight={500}>{admin?.email}</Typography>
          </Box>
          <Box sx={{ mx: 2, borderLeft: "1px solid", borderColor: "divider" }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Role</Typography>
            <Chip
              label={admin?.role === 1 ? "Super Admin" : "Subadmin"}
              size="small"
              color={admin?.role === 1 ? "primary" : "default"}
              sx={{ ml: 0.5 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* ── Purge confirmation dialog ── */}
      <Dialog open={!!purgeTarget} onClose={() => !purging && setPurgeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {purgeTarget === "articles" ? "Purge Articles" : "Purge Dedup Logs"}
        </DialogTitle>
        <DialogContent>
          {purgeResult ? (
            purgeResult.error
              ? <Alert severity="error">{purgeResult.error}</Alert>
              : <Alert severity="success">
                  Deleted <strong>{purgeResult.deleted.toLocaleString()}</strong> records.
                </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {purgeTarget === "articles" ? (
                  <>
                    Total articles stored: <strong>{stats?.total_articles?.toLocaleString() ?? "—"}</strong>.
                    {" "}Select how far back to keep. Everything older will be permanently deleted.
                  </>
                ) : (
                  <>
                    Total dedup logs stored: <strong>{(dedup?.total_dedup_logs ?? 0).toLocaleString()}</strong>.
                    {" "}Select how far back to keep. Everything older will be permanently deleted.
                  </>
                )}
              </DialogContentText>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(purgeTarget === "articles" ? ARTICLE_PURGE_PRESETS : DEDUP_PURGE_PRESETS).map(p => (
                  <Chip
                    key={p.label}
                    label={p.label}
                    onClick={() => setPurgeDays(p.value)}
                    color={purgeDays === p.value ? (p.value === "" ? "error" : "primary") : "default"}
                    variant={purgeDays === p.value ? "filled" : "outlined"}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color={purgeDays === "" ? "error.main" : "text.secondary"} sx={{ mt: 1.5, display: "block" }}>
                {purgeDays
                  ? `Will permanently delete records older than ${purgeDays} days`
                  : "Will delete ALL records — this cannot be undone"}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeTarget(null)} disabled={purging}>Cancel</Button>
          {!purgeResult && (
            <Button onClick={handlePurge} color="error" variant="contained" disabled={purging}>
              {purging ? "Purging…" : "Confirm Purge"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Base>
  );
};

export default Dashboard;
